<!--
  DESCRIPTION: This component is a dependent select that displays a list of options based on a selected value from another select component. It receives the following props:
    - field: an object that contains the field properties (label, mandatory, multivalued, helpText, options, optionsByReference, readonly, sort).
    - value: the current value of the select.
    - dependentValue: the current value of the select that this component depends on.
  USAGE:
    <DependentSelect
      :field="field"
      :value="value"
      :dependentValue="dependentValue"
      @input="handleInput"
    />
-->
<template>
  <div>
    <v-select v-model="newValue" v-bind="currentProperties()" />
  </div>
</template>

<script setup>
import { useStore } from 'vue2-helpers/vuex';
import { ref, watch } from 'vue';
import { sortSelect } from '../../utils';

// eslint-disable-next-line no-undef
const emit = defineEmits(['input']);

// eslint-disable-next-line no-undef
const props = defineProps({
  field: { type: Object, default: () => { } },
  value: { type: [Object, Array, String], default: null },
  dependentValue: { type: String, default: null }
});

const store = useStore();

const newValue = ref(props.value);

const currentProperties = () => {
  const properties = {};

  // General properties

  properties.readonly = props.field.readonly;

  properties['background-color'] = 'secondary';

  properties['menu-props'] = { light: true };

  properties.items = [];

  if (props.dependentValue) {
    if (props.field.optionsByReference) {
      const options = store.getters.getReference(props.field.optionsByReference)
            || [];
      Object.entries(options).forEach(([value, label]) => {
        if (
          label.meta.dependentSelect.showInListWhenDependentValueIs.includes(
            props.dependentValue
          )
        ) {
          properties.items.push({
            value: value,
            text:
                  typeof label.label === 'object'
                    ? label.label[store.getters.locale]
                    : label.label
          });
        }
      });
    } else {
      properties.items = props.field.options[props.dependentValue];
    }
  }
  let mandatory = true;
  if (!properties.items || properties.items.length === 0) {
    properties.disabled = true;
    mandatory = false;
  } else if (props.field.sort) {
    properties.items = sortSelect(
      properties.items,
      props.field.sort,
      'text'
    );
  }

  properties.rules = [];
  properties.label = props.field.label;

  if (props.field.overrideMandatoryWhenEmpty && mandatory) {
    properties.label += ' *';

    if (props.field.multivalued) {
      properties.rules.push(
        v => v.length !== 0 || 'Ce champ est obligatoire'
      );
    } else {
      properties.rules.push(v => !!v || 'Ce champ est obligatoire');
    }
  }

  properties.hint = props.field.helpText;
  properties.multiple = props.field.multivalued;

  if (!properties.readonly) {
    properties.clearable = true;
  }

  properties['item-color'] = 'info';

  return properties;
};

watch(() => props.dependentValue, () => {
  newValue.value = null;
});
watch(() => props.value, value => {
  newValue.value = value;
});
watch(newValue, value => {
  emit('input', value);
});

</script>
